/**
 * Ink-brush effect palettes (C0-VFX, docs/research/C0_VFX.md): ink black from the master's palette,
 * with ONE accent colour per move kind, the same language as the move icons (docs/research/C0_ICONS.md):
 * attack vermilion, break amber, power gold, heal jade, buff sky.
 */
export type InkKind = 'attack' | 'break' | 'power' | 'heal' | 'buff';
/** Palettes: the move kinds, plus the extra tones the style comparison uses (render/ink/styles.ts). */
export type InkTone = InkKind | 'steel' | 'cinnabar' | 'wind' | 'leaf' | 'blossom' | 'blood' | 'earth' | QiTone | 'pixInk';
/**
 * C0-SKILLFX ink A/B/C samples (docs/research/C0_VFX.md §10.4): the strokes drawn as element qi instead of ink
 * (body in the element's mid tone, lit sheen, dark edge, pale wash; the halo the element's light), and the "pixel
 * ink" for big moments only (indigo-black with a cinnabar accent instead of pure black).
 */
export type QiTone = 'qiWind' | 'qiCloud' | 'qiBlood' | 'qiInk';

export interface InkPalette {
  /** Ink body, its lit sheen, the stroke's dark edge, and the diluted wash it bleeds to as it fades. */
  ink: string;
  inkLit: string;
  edge: string;
  wash: string;
  /** The kind's accent: mid, light (wet tip, lit rim), dark (rim of an accent-bodied stroke). */
  acc: string;
  accLit: string;
  accDark: string;
}

const INK = { ink: '#1d1820', inkLit: '#3b3142', edge: '#0d0a10', wash: '#6a5d6c' } as const;

export const INK_PALETTES: Readonly<Record<InkTone, InkPalette>> = {
  attack: { ...INK, acc: '#e24a34', accLit: '#ffa27a', accDark: '#962a20' },
  break: { ...INK, ink: '#2a1a12', inkLit: '#4a3020', wash: '#8a6440', acc: '#f59a2c', accLit: '#ffd98a', accDark: '#a95a14' },
  power: { ...INK, acc: '#f6cf4a', accLit: '#fff4b8', accDark: '#a8821e' },
  heal: { ...INK, ink: '#173528', inkLit: '#2c5a44', edge: '#0b1d15', wash: '#5f9d7c', acc: '#55cf72', accLit: '#c4f7cf', accDark: '#2b8248' },
  steel: { ink: '#1c2433', inkLit: '#34405a', edge: '#0c1018', wash: '#7a8aa8', acc: '#cfe4ff', accLit: '#ffffff', accDark: '#5a7aa8' },
  cinnabar: { ...INK, ink: '#2a0f10', acc: '#ff5a3a', accLit: '#ffd0a0', accDark: '#8a1a14' },
  wind: { ink: '#1c3a3c', inkLit: '#2e5a5c', edge: '#0e2022', wash: '#7fbfb8', acc: '#9fe8e0', accLit: '#f0fffc', accDark: '#3a9a98' },
  leaf: { ...INK, acc: '#7fcf5a', accLit: '#d8f5a0', accDark: '#3a7a2a' },
  blossom: { ...INK, acc: '#f5a0c0', accLit: '#ffe4ee', accDark: '#b0507a' },
  // Blood on a sword hit (the user, 2026-10-10): bright red, a dark edge, a light edge on the lit side.
  blood: { ink: '#5e0a12', inkLit: '#7e0d16', edge: '#3a0508', wash: '#a8303a', acc: '#d81e2c', accLit: '#ff8f7a', accDark: '#7e0d16' },
  // Debris thrown up from the ground (render/ink/spectacle.ts).
  earth: { ink: '#5a4630', inkLit: '#7a6248', edge: '#2e2218', wash: '#a08a6a', acc: '#a8865a', accLit: '#d8c09a', accDark: '#5a4630' },
  qiWind: { ink: '#2a8a74', inkLit: '#7fdcb8', edge: '#123a36', wash: '#b8f0d8', acc: '#d8fff0', accLit: '#ffffff', accDark: '#2a6e62' },
  qiCloud: { ink: '#8a90c4', inkLit: '#dfe4fa', edge: '#34365a', wash: '#c8cce8', acc: '#ffffff', accLit: '#ffffff', accDark: '#5f6496' },
  qiBlood: { ink: '#b81626', inkLit: '#f04a3a', edge: '#3a0508', wash: '#e07a6a', acc: '#ffb08a', accLit: '#fff0e0', accDark: '#6e0a14' },
  qiInk: { ink: '#3a3570', inkLit: '#6a64a8', edge: '#14122e', wash: '#9a96c8', acc: '#d8d4f4', accLit: '#ffffff', accDark: '#2a2650' },
  pixInk: { ink: '#1a1838', inkLit: '#2e2c5a', edge: '#0c0b1e', wash: '#5a5880', acc: '#e0402a', accLit: '#ff9a6a', accDark: '#8a1a14' },
  buff: { ...INK, ink: '#14243a', inkLit: '#2a4566', edge: '#0a1220', wash: '#5f84a8', acc: '#4fb0f0', accLit: '#c4e8ff', accDark: '#2a6fa8' },
};

/** '#rrggbb' as 0xAABBGGRR (little-endian ImageData word), opaque. */
export function word(hex: string): number {
  const v = parseInt(hex.slice(1), 16);
  return (0xff << 24) | ((v & 0xff) << 16) | (v & 0xff00) | ((v >> 16) & 0xff);
}
